import React, { useState, useEffect } from 'react';
import { Box, styled } from '@mui/material';

interface FlappingCardProps {
  productImage: string;
  usageImage: string;
  onClick?: () => void;
}

const CardContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  perspective: '1200px',
  cursor: 'pointer',
  userSelect: 'none',
}));

const CardInner = styled(Box)<{ isFlipped: boolean }>(({ isFlipped }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  transformStyle: 'preserve-3d',
  transition: 'transform 0.9s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
  '&:hover': {
    transform: isFlipped ? 'rotateY(180deg) scale(1.02)' : 'rotateY(0deg) scale(1.02)',
  },
}));

const CardFace = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
  border: '2px solid rgba(255,255,255,0.1)',
});

const CardFront = styled(CardFace)({
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transform: 'rotateY(0deg)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
  },
});

const CardBack = styled(CardFace)({
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transform: 'rotateY(180deg)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
  },
});

const FlappingCard: React.FC<FlappingCardProps> = ({ productImage, usageImage, onClick }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  // Auto-flip between product and usage images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlipped((prev) => !prev);
    }, 3000); // Flip every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const handleClick = () => {
    setIsFlipped(!isFlipped);
    if (onClick) {
      onClick();
    }
  };

  // Format URLs - React's public folder serves files from root
  const formatImageUrl = React.useCallback((url: string) => {
    try {
      // Ensure URL starts with /
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      
      // Split path and encode each segment properly (except the leading /)
      const parts = cleanUrl.split('/');
      const encodedParts = parts.map((part, index) => {
        if (index === 0 || !part) return part; // Keep leading slash and empty parts
        return encodeURIComponent(part);
      });
      
      const encoded = encodedParts.join('/');
      console.log('URL encoding:', url, '->', encoded);
      return encoded;
    } catch (e) {
      console.error('Error encoding URL:', url, e);
      return url; // Fallback to original
    }
  }, []);

  const encodedProductImage = formatImageUrl(productImage);
  const encodedUsageImage = formatImageUrl(usageImage);

  // Preload images to check if they exist
  React.useEffect(() => {
    console.log('Attempting to load images:');
    console.log('Product:', encodedProductImage);
    console.log('Usage:', encodedUsageImage);
    
    const img1 = new Image();
    img1.onload = () => console.log('✓ Product image loaded:', encodedProductImage);
    img1.onerror = (e) => {
      console.error('✗ Product image failed:', encodedProductImage);
      console.error('Original path:', productImage);
      console.error('Trying alternative encoding...');
      // Try with process.env.PUBLIC_URL if available
      const altPath = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}${productImage}` : productImage;
      console.log('Alternative path:', altPath);
    };
    img1.src = encodedProductImage;

    const img2 = new Image();
    img2.onload = () => console.log('✓ Usage image loaded:', encodedUsageImage);
    img2.onerror = (e) => {
      console.error('✗ Usage image failed:', encodedUsageImage);
      console.error('Original path:', usageImage);
    };
    img2.src = encodedUsageImage;
  }, [encodedProductImage, encodedUsageImage, productImage, usageImage]);

  // Use process.env.PUBLIC_URL to ensure correct path resolution
  // React's public folder files are served from root, so we need to ensure proper encoding
  const getImageSrc = React.useCallback((path: string) => {
    // Remove leading slash
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    
    // Split and encode each segment
    const segments = cleanPath.split('/').filter(s => s);
    const encodedSegments = segments.map(seg => encodeURIComponent(seg));
    
    // Build the path - React serves public folder files from root
    const encoded = '/' + encodedSegments.join('/');
    
    console.log('Building image src:', path, '->', encoded);
    return encoded;
  }, []);

  const productSrc = getImageSrc(productImage);
  const usageSrc = getImageSrc(usageImage);

  return (
    <CardContainer onClick={handleClick}>
      <CardInner isFlipped={isFlipped}>
        <CardFront>
          <img 
            src={productSrc} 
            alt="Product"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              console.error('Image load error for:', productSrc);
              console.error('Full URL attempted:', window.location.origin + productSrc);
              // Don't try fallback as it will cause infinite loop
            }}
            onLoad={() => console.log('✓ Product image loaded successfully:', productSrc)}
          />
        </CardFront>
        <CardBack>
          <img 
            src={usageSrc} 
            alt="Usage"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              console.error('Image load error for:', usageSrc);
              console.error('Full URL attempted:', window.location.origin + usageSrc);
            }}
            onLoad={() => console.log('✓ Usage image loaded successfully:', usageSrc)}
          />
        </CardBack>
      </CardInner>
    </CardContainer>
  );
};

export default FlappingCard;

